import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, Label, OverviewHeader } from '@brighterion/br-ui';
import { FieldArray, useFormikContext } from 'formik';
import clsx from 'clsx';
import { RULE_INPUT_PARAMETER_NAMES } from 'Common/enums';
import { RoleService } from 'Services';
import { shouldShowAddToList, shouldShowExecutionOrder, shouldShowSetFlags } from 'Selectors';
import { handleKeyEvent, handleOnClickEvent } from 'Utils';
import { ROUTES } from 'Components/Breadcrumbs/routes';
import { FormikFieldTextArea } from 'Components/FormikComponents';
import Conclusion from './Conclusion';
import Condition from './Condition';
import { isConditionFilled, isAllConditionsFilled } from './Condition/helper';
import MissingParameterModal from './MissingParameterModal/MissingParameterModal';
import { getMissingParamFieldNames } from './helper';

import styles from './RuleStatement.css';

type RuleStatementProps = {
    parameterData?: UseGetParametersResponse;
    listData: ListApiGetResponse;
    disabledInputFields: DisabledInputFields;
    conclusionFlagListData: ConclusionListApiGetResponse[];
};

const RuleStatement = ({
    parameterData,
    listData,
    disabledInputFields,
    conclusionFlagListData
}: RuleStatementProps) => {
    const { values, initialValues, isSubmitting, handleChange, handleBlur } = useFormikContext<NewRuleFormSchema>();
    const { t } = useTranslation();
    const history = useHistory();
    const showExecutionOrder = shouldShowExecutionOrder();
    const createConditionId = (values.conditions[values.conditions.length - 1].conditionId ?? 0) + 1;
    const newCondition: CustomConditionObj = {
        conditionId: createConditionId,
        parameterField: { name: [], type: '' },
        operatorField: [],
        valueField: '',
        valueType: '',
        readOnly: false
    };

    const createAction = {
        actionId: 0,
        values: '',
        operator: ''
    };
    const overridesExist = initialValues._custom?.overrideExist;
    const { isThresholdEditorOnly } = RoleService;
    const isVisible = !(overridesExist || isThresholdEditorOnly);
    const readOnly = isThresholdEditorOnly || overridesExist || disabledInputFields.disableDefinitionChange;
    const [showModal, setShowModal] = React.useState(false);
    const [missingParams, setMissingParams] = React.useState<string[]>([]);
    // only check if there's missing parameter on the initial load of the component
    useEffect(() => {
        const missingParamFieldNames = getMissingParamFieldNames(values.conditions, parameterData);

        if (missingParamFieldNames.length > 0) {
            setMissingParams(missingParamFieldNames);
            setShowModal(true);
        }
    }, [values.conditions, parameterData]);

    return (
        <>
            {showModal && (
                <MissingParameterModal
                    isOpen={showModal}
                    missingParams={missingParams}
                    closeModal={() => {
                        setShowModal(false);
                        history.push(ROUTES.GLOBAL_RULES.INDEX);
                    }}
                />
            )}
            <OverviewHeader dataTestId='rule-stmt' leftTitle={t('Rule Statement')}>
                <div className={styles.conclusionMessageLabel}>
                    <Label label={t('ruleConditionInfo')} id='condition-info' />
                </div>

                <div className={styles.sectionHeader}>{t('Conditions')}</div>
                <div className={styles.conditionWrapper}>
                    {}
                    <div className={styles.conditionHeaders}>
                        <div>Parameter</div>
                        <div>Operator</div>
                        <div>Value</div>
                        {isVisible && <div>Read-only</div>}
                    </div>

                    <FieldArray
                        name='conditions'
                        render={(conditionsArrayHelper) => (
                            <>
                                {values.conditions.map((condition: CustomConditionObj, index) => {
                                    const handleRemoveCondition = (i: number) => {
                                        if (i === 0) {
                                            conditionsArrayHelper.replace(i, newCondition);
                                        } else {
                                            conditionsArrayHelper.remove(i);
                                        }
                                    };

                                    return (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <div key={index} className={styles.conditionRow}>
                                            <Condition
                                                index={index}
                                                condition={condition}
                                                parameterData={parameterData}
                                                listData={listData}
                                                arrayHelper={conditionsArrayHelper}
                                                overridesExist={overridesExist}
                                                isThresholdEditorOnly={isThresholdEditorOnly}
                                                disabledInputFields={disabledInputFields}
                                            />
                                            {isVisible && disabledInputFields.disableDefinitionChange !== true && (
                                                <Icon
                                                    className={clsx(
                                                        styles.removeBtn,
                                                        ((!isConditionFilled(condition) && index === 0) ||
                                                            isSubmitting) &&
                                                            styles.removeBtnDisabled
                                                    )}
                                                    name='feature-remove'
                                                    size='md'
                                                    onClick={handleOnClickEvent(() => {
                                                        handleRemoveCondition(index);
                                                    })}
                                                    onKeyDown={handleKeyEvent(() => {
                                                        handleRemoveCondition(index);
                                                    })}
                                                />
                                            )}
                                        </div>
                                    );
                                })}

                                {isVisible && disabledInputFields.disableDefinitionChange !== true && (
                                    <button
                                        type='button'
                                        tabIndex={0}
                                        aria-disabled={!isAllConditionsFilled(values.conditions) || isSubmitting}
                                        disabled={!isAllConditionsFilled(values.conditions) || isSubmitting}
                                        className={styles.addConclusionButton}
                                        onKeyDown={handleKeyEvent(() => {
                                            if (isAllConditionsFilled(values.conditions) && !isSubmitting) {
                                                conditionsArrayHelper.push(newCondition);
                                            }
                                        })}
                                        onClick={handleOnClickEvent(() => {
                                            if (isAllConditionsFilled(values.conditions) && !isSubmitting) {
                                                conditionsArrayHelper.push(newCondition);
                                            }
                                        })}
                                    >
                                        <Icon name='add-file' size='md' />
                                        <span>{t('Add Condition')}</span>
                                    </button>
                                )}
                            </>
                        )}
                    />
                </div>

                <div className={styles.sectionHeader}>{t('Conclusion')}</div>

                <div className={styles.conditionWrapper}>
                    {!showExecutionOrder && (
                        <div className={styles.messageLabel}>
                            <Label className={styles.messageLabel} label={t('ruleConclusionRejectInfo')} id='info' />
                        </div>
                    )}

                    <FieldArray
                        name='actions'
                        render={(actionsArrayHelper) => (
                            <>
                                {values.actions.map((action, index) => {
                                    const handleRemoveAction = (i: number) => {
                                        if (i === 0) {
                                            actionsArrayHelper.replace(i, createAction);
                                        } else {
                                            actionsArrayHelper.remove(i);
                                        }
                                    };

                                    return (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <div key={index} className={styles.conditionRow}>
                                            <Conclusion
                                                index={index}
                                                conclusionFlagListData={conclusionFlagListData}
                                                parameterData={parameterData}
                                                listData={listData.result}
                                                readOnly={readOnly}
                                            />
                                            {isVisible && disabledInputFields.disableDefinitionChange !== true && (
                                                <Icon
                                                    aria-disabled={action.operator === '' && index === 0}
                                                    className={clsx(
                                                        styles.removeBtn,
                                                        isSubmitting ||
                                                            (action.operator === '' &&
                                                                index === 0 &&
                                                                styles.removeBtnDisabled)
                                                    )}
                                                    name='feature-remove'
                                                    size='md'
                                                    onClick={handleOnClickEvent(() => {
                                                        handleRemoveAction(index);
                                                    })}
                                                    onKeyDown={handleKeyEvent(() => {
                                                        handleRemoveAction(index);
                                                    })}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                                {isVisible &&
                                    !disabledInputFields.disableDefinitionChange &&
                                    (shouldShowSetFlags() || shouldShowAddToList()) && (
                                        <button
                                            type='button'
                                            tabIndex={0}
                                            aria-disabled={!values.actions[0].operator}
                                            disabled={!values.actions[0].operator}
                                            className={styles.addConclusionButton}
                                            onKeyDown={handleKeyEvent(() => actionsArrayHelper.push(createAction))}
                                            onClick={handleOnClickEvent(() => actionsArrayHelper.push(createAction))}
                                        >
                                            <Icon name='add-file' size='md' />
                                            <span>{t('Add Action')}</span>
                                        </button>
                                    )}
                            </>
                        )}
                    />
                </div>

                <div className={styles.sectionHeader}>{t('Comment (Optional)')}</div>
                <FormikFieldTextArea
                    id={RULE_INPUT_PARAMETER_NAMES.COMMENT}
                    name={RULE_INPUT_PARAMETER_NAMES.COMMENT}
                    className={styles.commentField}
                    lineNum={1}
                    maxChar={150}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                />
            </OverviewHeader>
        </>
    );
};

export default RuleStatement;
